import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ACTIVITY_WRITE_THROTTLE_MS,
  LAST_ACTIVITY_KEY,
  type SessionPhase,
  getSessionPhase,
  secondsUntilLogout,
} from '../models/session-inactivity.model';

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

const readLastActivity = (): number => {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : Date.now();
};

interface InactivityState {
  /** Fase actual: active (sin modal), warning (modal visible), expired. */
  phase: SessionPhase;
  /** Segundos restantes hasta el cierre (para el countdown del modal). */
  secondsLeft: number;
  /** Reinicia el contador en todas las pestañas (acción "Sigo aquí"). */
  stayActive: () => void;
}

/**
 * Cierra la sesión tras IDLE_LIMIT_MS de inactividad, avisando WARNING_MS antes.
 * Es ortogonal al auto-refresh del access token: acá el cierre es deliberado por
 * inactividad. La última actividad se comparte entre pestañas vía localStorage,
 * así trabajar en una pestaña mantiene viva la sesión en todas.
 *
 * @param onExpire callback al vencer (debe hacer logout real + redirect).
 */
export function useInactivityTimeout(onExpire: () => void): InactivityState {
  const [phase, setPhase] = useState<SessionPhase>('active');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const lastWriteRef = useRef(0);
  const expiredRef = useRef(false);
  // Espejo de `phase` para leerlo dentro de los listeners sin recrearlos.
  const phaseRef = useRef<SessionPhase>('active');
  phaseRef.current = phase;

  const writeActivity = useCallback((ts: number) => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
  }, []);

  const stayActive = useCallback(() => {
    const now = Date.now();
    lastWriteRef.current = now;
    writeActivity(now);
    setPhase('active');
  }, [writeActivity]);

  // Re-evalúa la fase contra reloj de pared (no por conteo de ticks).
  const evaluate = useCallback(() => {
    if (expiredRef.current) return;
    const now = Date.now();
    const last = readLastActivity();
    const nextPhase = getSessionPhase(last, now);
    setPhase(nextPhase);
    setSecondsLeft(secondsUntilLogout(last, now));
    if (nextPhase === 'expired') {
      expiredRef.current = true;
      onExpire();
    }
  }, [onExpire]);

  useEffect(() => {
    // Semilla: si no hay registro previo, arranca ahora.
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      stayActive();
    }

    // Actividad del usuario: registra (throttled). Durante el aviso NO se
    // reinicia por movimiento incidental — el cierre debe ser una decisión
    // explícita (botón / interacción con el modal vía stayActive).
    const onActivity = () => {
      if (phaseRef.current !== 'active') return;
      const now = Date.now();
      if (now - lastWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
        lastWriteRef.current = now;
        writeActivity(now);
      }
    };

    // Otra pestaña actualizó la actividad → re-evaluar (cierra el modal acá).
    const onStorage = (e: StorageEvent) => {
      if (e.key === LAST_ACTIVITY_KEY) evaluate();
    };

    // Al volver el foco a una pestaña en background, re-evaluar ya (los timers
    // pudieron estar throttled y la ventana pudo vencer mientras tanto).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') evaluate();
    };

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);

    const interval = window.setInterval(evaluate, 1000);
    evaluate();

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, [evaluate, stayActive, writeActivity]);

  return { phase, secondsLeft, stayActive };
}
